import React, { useState } from 'react';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Chip,
  Box,
  CircularProgress,
} from '@mui/material';
import { useNotify, useRecordContext } from 'react-admin';
import { getToken } from '../authentication/authProvider';

const PAPERMAN_API_URL = process.env.REACT_APP_PAPERMAN_API_URL;

type Variant = 'stripe' | 'manual';

type ResendReceiptButtonProps = {
  /** The contract these payments belong to. */
  contractId?: number | string;
  /** 'stripe' rows carry a charge/PI; 'manual' rows carry amount + date only. */
  variant: Variant;
  label?: string;
};

type Draft = {
  to: string;
  subject: string;
  body: string;
  fullyPaid: boolean;
  remainingBalance: number;
};

/**
 * (Re)send the payment-receipt email to the buyer for a single payment row.
 * Opens an editable email modal (To / Subject / Message) prefilled from the
 * backend draft, then sends. Stripe rows pass the charge/PI so the backend pulls
 * real card details for the attached PDF; manual rows pass amount + date.
 */
const ResendReceiptButton = ({ contractId, variant }: ResendReceiptButtonProps) => {
  const record = useRecordContext<any>();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  if (!record) return null;

  const attachmentBody = () => {
    if (variant === 'stripe') {
      // Card/receipt details were stored on the payment row at processing time;
      // the backend reads them by id (no Stripe call).
      return { paymentId: record.id };
    }
    // Manual / QuickBooks payment: TotalAmt is in dollars.
    return {
      amountCents: Math.round(Number(record.TotalAmt || 0) * 100),
      paidAt: record.TxnDate,
    };
  };

  const handleOpen = async () => {
    if (!contractId) {
      notify('Contract not available for this payment', { type: 'error' });
      return;
    }
    setOpen(true);
    setLoadingDraft(true);
    try {
      const res = await fetch(`${PAPERMAN_API_URL}contract/${contractId}/receipt/draft`, {
        headers: { Authorization: `Bearer ${await getToken()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d: Draft = await res.json();
      setDraft(d);
      setTo(d.to);
      setSubject(d.subject);
      setMessage(d.body);
    } catch (e: any) {
      notify(`Could not load the receipt draft: ${e.message ?? e}`, { type: 'error' });
    } finally {
      setLoadingDraft(false);
    }
  };

  const handleSend = async () => {
    if (!contractId) return;
    setSending(true);
    try {
      const res = await fetch(`${PAPERMAN_API_URL}contract/${contractId}/receipt/resend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await getToken()}`,
        },
        body: JSON.stringify({ ...attachmentBody(), to: to.trim(), subject: subject.trim(), message }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({} as any));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      notify(`Receipt emailed to ${to.trim()}`, { type: 'success' });
      setOpen(false);
    } catch (e: any) {
      notify(`Failed to send receipt: ${e.message ?? e}`, { type: 'error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        size="small"
        startIcon={<EmailIcon />}
        onClick={(e) => { e.stopPropagation(); void handleOpen(); }}
        aria-label="Resend payment receipt"
      >
        Resend receipt
      </Button>
      <Dialog open={open} onClose={() => !sending && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Resend payment receipt</DialogTitle>
        <DialogContent dividers>
          {loadingDraft ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2} sx={{ pt: 1 }}>
              {draft && (
                <Box>
                  <Chip
                    size="small"
                    color={draft.fullyPaid ? 'success' : 'warning'}
                    label={
                      draft.fullyPaid
                        ? 'Paid in full — stamped receipt'
                        : `Partial — balance $${draft.remainingBalance.toFixed(2)} remaining`
                    }
                  />
                </Box>
              )}
              <TextField
                label="To (email)"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                disabled={sending}
                type="email"
                required
                fullWidth
                size="small"
              />
              <TextField
                label="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={sending}
                fullWidth
                size="small"
              />
              <TextField
                label="Message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={sending}
                fullWidth
                multiline
                minRows={7}
                helperText="The receipt PDF is attached automatically."
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={sending}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void handleSend()}
            disabled={sending || loadingDraft || !to.trim()}
            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          >
            {sending ? 'Sending…' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ResendReceiptButton;
